import { useEffect, useRef, useState } from 'react';
import type { DevPanelDeps, SyncJobRow } from './types';

/**
 * Dev-only tools shown beneath the login form when VITE_ENV === 'dev':
 *   • Reseed dev user — POSTs reseedEndpoint (re-runs seed.sql on the connected
 *     branch, restoring dev@eb.local).
 *   • Clone from Production — POSTs syncEndpoint, then polls sync_jobs (via the
 *     injected pollSyncJob) until done/error. After it finishes, sign in with
 *     your prod email/password.
 * Both act on whatever Supabase branch THIS deployment points at (its own per-PR
 * branch on a preview), so they're isolated per environment.
 */
export function DevPanels({ dev }: { dev: DevPanelDeps }) {
  if (!dev.isDev) return null;
  return (
    <div className="mt-6 border-t border-neutral-200 pt-5 dark:border-neutral-800">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Dev tools</h3>
        {dev.dbBranchLabel && (
          <span className="rounded bg-neutral-100 px-2 py-0.5 font-mono text-[10px] text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400">
            {dev.dbBranchLabel}
          </span>
        )}
      </div>
      <ReseedPanel endpoint={dev.reseedEndpoint} />
      <SyncPanel endpoint={dev.syncEndpoint} pollSyncJob={dev.pollSyncJob} />
    </div>
  );
}

type ReseedStep = 'idle' | 'seeding' | 'done' | 'error';

function ReseedPanel({ endpoint }: { endpoint: string }) {
  const [step, setStep] = useState<ReseedStep>('idle');
  const [error, setError] = useState<string | null>(null);

  const reseed = async () => {
    setStep('seeding');
    setError(null);
    try {
      const res = await fetch(endpoint, { method: 'POST' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: 'Re-seed failed' }));
        throw new Error(data.error || 'Re-seed failed');
      }
      setStep('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Re-seed failed');
      setStep('error');
    }
  };

  return (
    <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-900/60 dark:bg-blue-950/30">
      <div className="text-sm font-medium text-blue-800 dark:text-blue-300">Re-seed dev user</div>
      <div className="mb-2 text-xs text-blue-700 dark:text-blue-200/70">
        Re-runs <code>seed.sql</code> on the connected branch. Restores{' '}
        <code>dev@eb.local</code> / <code>devpassword123</code> if a prior clone wiped it.
      </div>
      {step === 'idle' && (
        <button
          onClick={reseed}
          className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-blue-500"
        >
          Re-seed
        </button>
      )}
      {step === 'seeding' && <p className="text-sm text-blue-700 dark:text-blue-300">Seeding…</p>}
      {step === 'done' && (
        <p className="text-sm text-green-700 dark:text-green-400">
          Done. Sign in with <code>dev@eb.local</code> / <code>devpassword123</code>.
        </p>
      )}
      {step === 'error' && (
        <>
          <p className="whitespace-pre-wrap break-words text-sm text-red-600 dark:text-red-400">{error}</p>
          <button
            onClick={() => setStep('idle')}
            className="mt-2 rounded-md border border-neutral-300 px-3 py-1 text-sm text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800"
          >
            Try again
          </button>
        </>
      )}
    </div>
  );
}

type SyncStep = 'idle' | 'confirm' | 'syncing' | 'done' | 'error';

function SyncPanel({
  endpoint,
  pollSyncJob,
}: {
  endpoint: string;
  pollSyncJob: (sinceISO: string | null) => Promise<SyncJobRow | null>;
}) {
  const [step, setStep] = useState<SyncStep>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [job, setJob] = useState<SyncJobRow | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAtRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const poll = async () => {
    const latest = await pollSyncJob(startedAtRef.current);
    if (!latest) return;
    setJob(latest);
    if (latest.status === 'done') {
      if (pollRef.current) clearInterval(pollRef.current);
      if (tickRef.current) clearInterval(tickRef.current);
      setStep('done');
    } else if (latest.status === 'error') {
      if (pollRef.current) clearInterval(pollRef.current);
      if (tickRef.current) clearInterval(tickRef.current);
      setError(latest.error_message || 'Clone failed');
      setStep('error');
    }
  };

  const handleSync = async () => {
    setStep('syncing');
    setError(null);
    setElapsed(0);
    setJob(null);
    startedAtRef.current = new Date().toISOString();
    tickRef.current = setInterval(() => setElapsed((n) => n + 1), 1000);
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok && res.status !== 202) {
        const data = await res.json().catch(() => ({ error: 'Clone failed' }));
        throw new Error(data.error || 'Clone failed');
      }
      pollRef.current = setInterval(poll, 2000);
      void poll();
    } catch (err) {
      if (tickRef.current) clearInterval(tickRef.current);
      setError(err instanceof Error ? err.message : 'Clone failed');
      setStep('error');
    }
  };

  const reset = () => {
    if (tickRef.current) clearInterval(tickRef.current);
    if (pollRef.current) clearInterval(pollRef.current);
    setElapsed(0);
    setStep('idle');
    setJob(null);
    startedAtRef.current = null;
  };

  const resultRows = job && Object.keys(job.results).length > 0 ? Object.entries(job.results) : null;

  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900/60 dark:bg-red-950/20">
      {step === 'idle' && (
        <button onClick={() => setStep('confirm')} className="w-full text-left">
          <div className="text-sm font-medium text-red-700 dark:text-red-300">Clone from Production</div>
          <div className="text-xs text-red-600/80 dark:text-red-200/70">
            Wipe this branch's data + auth, replace with prod. Sign in with your prod
            email/password after.
          </div>
        </button>
      )}
      {step === 'confirm' && (
        <div>
          <p className="mb-1 text-sm font-medium text-red-700 dark:text-red-300">
            This DELETES all data + auth on this branch and replaces both with production.
          </p>
          <p className="mb-3 text-xs text-red-600/80 dark:text-red-200/70">
            After cloning, sign in with your production email and password. The seeded{' '}
            <code>dev@eb.local</code> account will no longer exist (use Re-seed to bring it back).
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleSync}
              className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-red-500"
            >
              Yes, wipe and clone prod
            </button>
            <button
              onClick={reset}
              className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {step === 'syncing' && (
        <div>
          <p className="text-sm font-medium text-neutral-800 dark:text-neutral-200">
            Cloning from production… {elapsed}s
          </p>
          <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
            Phase: {job?.current_phase || 'starting'}
          </p>
          {resultRows && (
            <ul className="mt-2 max-h-40 space-y-0.5 overflow-y-auto text-[11px] text-neutral-500 dark:text-neutral-400">
              {resultRows.map(([k, v]) => (
                <li key={k} className="flex justify-between">
                  <span>{k}</span>
                  <span className="font-mono">{v}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {step === 'done' && (
        <div>
          <p className="mb-1 text-sm font-medium text-green-700 dark:text-green-400">Clone complete</p>
          <p className="text-xs text-green-700/80 dark:text-green-300/80">
            Sign in with your production email and password to see your prod data.
          </p>
          <button
            onClick={reset}
            className="mt-3 rounded-md border border-neutral-300 px-3 py-1 text-sm text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800"
          >
            Done
          </button>
        </div>
      )}
      {step === 'error' && (
        <div>
          <p className="mb-1 text-sm font-medium text-red-600 dark:text-red-400">Clone failed</p>
          <p className="whitespace-pre-wrap break-words text-xs text-red-600/80 dark:text-red-300/80">{error}</p>
          <button
            onClick={reset}
            className="mt-3 rounded-md border border-neutral-300 px-3 py-1 text-sm text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800"
          >
            Try again
          </button>
        </div>
      )}
    </div>
  );
}
